<script setup lang="ts">
import { computed, inject } from 'vue'
import { useBanner } from '@/composables/useBanner'
import type { BannerState } from '@/composables/useBanner'

const bannerState = inject<BannerState>('announcementBanner') ?? useBanner()
const {
  showBanner,
  bannerText,
  bannerShortText,
  bannerBackgroundColor,
  bannerTextColor,
  bannerType,
  showClose,
  canClose,
  linkUrl,
  closeBanner
} = bannerState

const bannerIcon = computed(() => {
  switch (bannerType.value) {
    case 'error':
      return '❌'
    case 'warning':
      return '⚠️'
    case 'info':
      return '🚀'
    case 'success':
      return '💡'
    case 'hello':
    default:
      return '👋'
  }
})

const isClickable = computed(() => !!linkUrl.value)

const bannerStyle = computed(() => ({
  backgroundColor: bannerBackgroundColor.value,
  color: bannerTextColor.value,
  cursor: isClickable.value ? 'pointer' : 'default'
}))

function handleClick() {
  const url = linkUrl.value
  if (!url) return
  const hash = url.startsWith('#') ? url : (url.includes('#') ? '#' + url.split('#').pop() : '')
  if (hash) {
    const el = document.querySelector(hash)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
  }
  window.open(url, '_blank')
}
</script>

<template>
  <Transition name="banner-slide" @after-leave="bannerState.bannerAfterLeave">
    <div
      v-if="showBanner"
      class="notification-banner"
      :style="bannerStyle"
      :data-type="bannerType"
      @click="handleClick"
    >
      <div class="banner-content">
        <span class="banner-icon">{{ bannerIcon }}</span>
        <!-- When shortText is set: show short on phone, long on desktop/tablet. Otherwise show text (truncated). -->
        <span v-if="bannerShortText" class="banner-text banner-text-short">{{ bannerShortText }}</span>
        <span :class="['banner-text', { 'banner-text-long': !!bannerShortText }]">{{ bannerText }}</span>
      </div>
      <button
        v-if="showClose && canClose"
        class="banner-close-btn"
        @click.stop="closeBanner"
        :style="{ color: bannerTextColor }"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
/* Sits below the Safari strip; nav bars use --banner-offset so they start below this */
.notification-banner {
  position: fixed;
  top: var(--safari-strip-height, 20px);
  left: 0;
  right: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  font-size: 0.9375rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.notification-banner .banner-content,
.notification-banner .banner-close-btn {
  position: relative;
  z-index: 1;
}

/* Animate banner out when closing or autohide */
.banner-slide-enter-active,
.banner-slide-leave-active {
  transition: transform 0.3s ease-out, opacity 0.25s ease-out;
}
.banner-slide-enter-from,
.banner-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
.banner-slide-enter-from {
  opacity: 0;
}
.banner-slide-leave-to {
  opacity: 0;
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  overflow: hidden;
}

.banner-icon {
  flex-shrink: 0;
  font-size: 1.25rem;
}

.banner-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

/* When shortText is set: show short on phone (≤768px), long on desktop/tablet */
.banner-text-short {
  display: none;
}
.banner-text-long {
  display: inline;
}
@media (max-width: 768px) {
  .banner-text-short {
    display: inline;
  }
  .banner-text-long {
    display: none;
  }
}

.banner-close-btn {
  flex-shrink: 0;
  margin-left: 1rem;
  padding: 0.25rem;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.banner-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Type-specific styles */
:deep(.notification-banner[data-type="error"]) {
  background: #ef4444;
  color: #ffffff;
}

:deep(.notification-banner[data-type="warning"]) {
  background: #f59e0b;
  color: #ffffff;
}

:deep(.notification-banner[data-type="info"]) {
  background: #3b82f6;
  color: #ffffff;
}
</style>